<script setup lang="ts">
import {
    checkStripeAccountReady,
    currentMarketingStripeStatus,
} from '@partner-spa-vue/stores/CurrentWebsiteStripeStatusStore';
import { pageTitle } from '@partner-spa-vue/stores/LayoutStore';
import { toast } from '@/ui-plus/sonner';
import { currentWebsite, currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Badge from '@/components/ui/badge/Badge.vue';
import { zmgAdminOnlyCacheUpdateClient } from '@api-client/zmg_cache';
import { ChevronRight } from 'lucide-vue-next';

pageTitle.value = 'Affiliate Partnership';

const marketingPartnerLinks = [
    // { name: 'marketing-earnings-stripe', label: 'Business and bank details' },
    { name: 'marketing-earnings-stripe', label: 'Stripe' },
    { name: 'marketing-earnings-fee-settings', label: 'Fee Settings' },
    { name: 'marketing-earnings-balances', label: 'Balances' },
    { name: 'marketing-earnings-incoming', label: 'Incoming' },
    { name: 'marketing-earnings-unpaid', label: 'Unpaid' },
    { name: 'marketing-earnings-history', label: 'History' },
    { name: 'marketing-earnings-reports', label: 'Reports' },
    { name: 'marketing-earnings-notifications', label: 'Notifications' },
] as const;

const onPartnerChange = async (checked: boolean) => {
    if (currentWebsite.value) {
        currentWebsite.value.isMarketingTeamPartner = checked;
    }
    await saveChanges();
}

const saveChanges = async () => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.partner_info[':id'].$patch({
        param: { id: currentWebsiteId.value ?? '' },
        json: {
            isMarketingTeamPartner: currentWebsite.value?.isMarketingTeamPartner ?? false,
        }
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to update partner');
    } else {
        toast.success('Partner settings updated');
    }
}
</script>

<template>
    <div class="max-w-3xl mx-auto">
        <Card>
            <CardHeader>
                <CardTitle>
                    Affiliate Partnership
                </CardTitle>
                <CardDescription>
                    Perform marketing activities for stores listed on your website and earn money whenever
                    someone places an order through your website. You can list one or multiple stores and
                    promote them.
                    <br>
                    <br>
                    Use the sidebar to view balances, incoming amounts, your rate settings and
                    earnings history.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <label for="marketing-partner"
                       class="flex items-center justify-between p-4 border rounded-lg cursor-pointer">
                    <div class="space-y-0.5">
                        <h3 class="text-lg font-medium">Partner</h3>
                        <p class="text-muted-foreground">
                            Become a partner to list stores on your website and earn a fee from their orders.
                            <br>
                            <br>
                            <Badge v-if="currentWebsite?.defaultHostname"
                                   class="ml-2">
                                {{ currentWebsite?.defaultHostname }}
                            </Badge>
                            Orders placed through your website can earn you a marketing partner fee.
                            (check 'Fee settings' below to set)
                        </p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <Switch id="marketing-partner"
                                :checked="currentWebsite?.isMarketingTeamPartner ?? false"
                                @update:checked="onPartnerChange"
                                class="scale-125">
                        </Switch>
                        <span class="font-medium">{{ currentWebsite?.isMarketingTeamPartner ? 'Yes' : 'No' }}</span>
                    </div>
                </label>
                <template v-if="currentWebsite?.isMarketingTeamPartner">
                    <div class="border-t my-6"></div>
                    <nav class="border rounded-lg divide-y">
                        <RouterLink v-for="link in marketingPartnerLinks"
                                    :key="link.name"
                                    :to="{ name: link.name }"
                                    class="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                            <span class="font-medium">
                                {{ link.label }}
                                <span v-if="link.name === 'marketing-earnings-stripe'">
                                    {{ checkStripeAccountReady(currentMarketingStripeStatus) === false ? '⚠️' : '' }}
                                </span>
                            </span>
                            <ChevronRight class="h-4 w-4 text-muted-foreground" />
                        </RouterLink>
                    </nav>
                </template>
            </CardContent>
        </Card>
    </div>
</template>
